"use client";

import {
  createContext,
  useContext,
  useState,
  type ComponentType,
  type FocusEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import BirthToolFormShell, { type BirthToolFormShellProps } from "./BirthToolFormShell";

/**
 * The chart checks' one slot (WR-03): each check's boundary module keeps its
 * own dynamic() to its island and hands the result here with the shell's
 * props, the real form's field ids and the slot's reserve and data attributes.
 * The form, place search, engine client, answer and analytics stay in the
 * island's async chunk; the first load gains the boundary, this file and the
 * shell.
 *
 * Activation is element-scoped and local: no window listener and no timer, so
 * a reader scrolling the guide never downloads the island and the deploy's
 * body pointerdown smoke loads nothing; a press, focus or submit on the shell
 * does. The shell's own onActivate cannot tell a keyboard focus from a touch,
 * so the slot reads the intent in the capture phase: focus follows the visitor
 * into the real form after a keyboard focus, a mouse press or a submit, and
 * never after a touch, which may be the start of a scroll.
 *
 * The slot's class reserves the check's tallest state (each boundary records
 * its measured heights), so neither shell -> form nor a slow engine reply
 * moves the page below. translate="no" covers the shell, form and answer,
 * which re-render on "check another" (tests/translate-safe-islands.test.ts).
 */

/** The real form's input ids, which the shell's dob, tob and place inputs stand in for. */
interface ChartCheckFieldIds {
  date: string;
  time: string;
  place: string;
}

/**
 * The shell's props for this page. dynamic()'s loading placeholder receives
 * no props from the page, so the slot hands them down through context.
 */
const ShellProps = createContext<BirthToolFormShellProps | null>(null);

/** dynamic()'s loading placeholder: the page's shell, or `fallback` outside a slot. */
export function ChartCheckLoadingShell({ fallback }: { fallback: BirthToolFormShellProps }) {
  return <BirthToolFormShell {...(useContext(ShellProps) ?? fallback)} />;
}

export interface ChartCheckSlotProps<P> {
  /** The island, as the boundary's one dynamic() returns it. */
  island: ComponentType<P & { focusFieldId?: string | null }>;
  /** The island's own props; the slot adds `focusFieldId`. */
  islandProps: NoInfer<P>;
  shell: BirthToolFormShellProps;
  fieldIds: ChartCheckFieldIds;
  /** The slot's class, verbatim: its flex column and reserve floors. */
  className: string;
  /** The slot's data attributes, in order (its `data-<check>-slot` marker first). */
  data: Readonly<Record<`data-${string}`, string>>;
  /** Shown under the shell until the island mounts (the island renders its own). */
  afterShell?: ReactNode;
}

export default function ChartCheckSlot<P extends object>({
  island: Island,
  islandProps,
  shell,
  fieldIds,
  className,
  data,
  afterShell,
}: ChartCheckSlotProps<P>) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  /** BirthToolFormShell input names -> the real form's input ids. */
  function formFieldId(target: EventTarget | null): string {
    const name = target instanceof Element ? target.getAttribute("name") : null;
    const byName: Readonly<Record<string, string>> = {
      dob: fieldIds.date,
      tob: fieldIds.time,
      place: fieldIds.place,
    };
    return (name && byName[name]) || fieldIds.date;
  }

  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(fieldIds.date),
      };

  return (
    <div translate="no" {...data} className={className} {...shellIntent}>
      {active ? (
        <ShellProps.Provider value={shell}>
          <Island {...islandProps} focusFieldId={focusFieldId} />
        </ShellProps.Provider>
      ) : (
        <>
          <BirthToolFormShell {...shell} onActivate={() => setActive(true)} />
          {afterShell}
        </>
      )}
    </div>
  );
}
